import { Box, Typography, useTheme } from '@mui/material';
import {
  Eye,
  MessageSquare,
  ThumbsUp,
  Flame,
  BookOpen,
  Target,
  MessageCircle,
  BadgeCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePoints } from '@/hooks/usePoints';
import { getCardSx, getSmallIconStyles } from '@/theme/utils';
import { TYPOGRAPHY } from '@/pages/ListContentPage/constants';

type EarnAction = {
  icon: React.ElementType;
  labelKey: string;
  pts: number | undefined;
  iconColor: string;
};

export const HowToEarnSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { points_per_action } = usePoints();

  const actions: EarnAction[] = [
    {
      icon: Eye,
      labelKey: 'homeScreen.step1',
      pts: points_per_action?.finished_capsule,
      iconColor: theme.palette.primary.main,
    },
    {
      icon: MessageSquare,
      labelKey: 'homeScreen.step2',
      pts: points_per_action?.commented_capsule,
      iconColor: theme.palette.primary.main,
    },
    {
      icon: BookOpen,
      labelKey: 'homeScreen.step3',
      pts: points_per_action?.approved_test,
      iconColor: theme.palette.primary.main,
    },
    {
      icon: ThumbsUp,
      labelKey: 'homeScreen.step4',
      pts: points_per_action?.liked_comment,
      iconColor: theme.palette.primary.main,
    },
    {
      icon: Flame,
      labelKey: 'homeScreen.step5',
      pts: points_per_action?.streak,
      iconColor: theme.palette.fire.main,
    },
    {
      icon: BadgeCheck,
      labelKey: 'homeScreen.step6',
      pts: points_per_action?.knowledge_test_first_chance,
      iconColor: theme.palette.success.main,
    },
    {
      icon: Target,
      labelKey: 'homeScreen.step8',
      pts: points_per_action?.challenge_task_first_chance,
      iconColor: theme.palette.warning.main,
    },
    {
      icon: MessageCircle,
      labelKey: 'homeScreen.step10',
      pts: points_per_action?.commented_thread,
      iconColor: theme.palette.primary.main,
    },
  ].filter((a) => a.pts);

  return (
    <Box sx={{ ...getCardSx(theme), cursor: 'default' }}>
      <Typography
        sx={{
          fontFamily: TYPOGRAPHY.bold,
          fontSize: theme.fontSizes.subtitle,
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 2,
        }}
      >
        {t('pointsScreen.howToEarnPoints')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {actions.map(({ icon: Icon, labelKey, pts, iconColor }) => (
          <Box
            key={labelKey}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minHeight: '44px',
            }}
          >
            <Box sx={getSmallIconStyles(iconColor)}>
              <Icon size={16} color={iconColor} />
            </Box>
            <Typography
              sx={{
                flex: 1,
                fontFamily: 'Inter, sans-serif',
                fontSize: theme.fontSizes.body,
                color: theme.palette.text.primary,
              }}
            >
              {t(labelKey)}
            </Typography>
            <Box
              sx={{
                bgcolor: theme.palette.success.light,
                color: theme.palette.success.main,
                fontFamily: 'Inter, sans-serif',
                fontSize: theme.fontSizes.caption,
                fontWeight: 700,
                px: 1,
                py: 0.25,
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              +{pts} pts
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
